import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Layout from "@/components/Layout";
import ServiceModule from "@/components/ServiceModule";
import UpperGallery from "@/components/UpperGallery";
import ElevateJourneyBanner from "@/components/ElevateJourneyBanner";

const GALLERY_TILES = [
  { src: "/assets/services/gallery-interior.jpg", caption: "Interior Workspace" },
  { src: "/assets/services/gallery-fleet-trim.jpg", caption: "Luxury Fleet Trim" },
];

const SERVICE_MODULES = [
  {
    testid: "service-module-airports",
    eyebrow: "Service · 01",
    imageSrc: "/assets/services/module-a-airports.jpg",
    imageSide: "left",
    heading: "Executive Airport Transfers",
    body: "Airport transfers feature transparent fixed pricing based on distance from the terminal. To get an accurate quote for your specific needs, it's best to contact Phantasm Limousine directly and provide details about your trips. Our professional chauffeurs will meet you in the arrival lounge or the baggage claim with a digital sign displaying your name, welcoming you, and assisting you directly to the luxury limo pool area according to your preference.",
    chips: [
      "Boston Logan (BOS)",
      "Manchester Regional Airport (MHT)",
      "Bradley International Airport (BDL)",
      "Rhode Island T.F. Green International Airport (PVD)",
      "John F. Kennedy (JFK)",
    ],
    ctaHref: "/book?service=airport",
    fullBleedImage: true,
  },
  {
    testid: "service-module-corporate",
    eyebrow: "Service · 02",
    imageSrc: "/assets/services/module-b-corporate.jpg",
    imageSide: "right",
    heading: "Corporate Travel & Fleet",
    body: "We provide a highly professional service and a reliable fleet, ensuring you reach your destination safely and punctually for your next business engagement. Our luxury Cadillac Escalade ESV is optimized for executive transport, airport runs, concerts, and special events, offering versatile configurations for both passenger luggage and absolute comfort.",
    ctaHref: "/book?service=corporate",
    imagePosition: "object-top",
  },
  {
    testid: "service-module-ports",
    eyebrow: "Service · 03",
    imageSrc: "/assets/services/module-c-ports.jpg",
    imageSide: "left",
    heading: "Regional Cruise Ports & Ground Travel",
    body: "Door-to-door luxury transport to Black Falcon Cruise Port and long-distance regional transfers throughout New England. Our chauffeurs handle the logistics — you arrive composed.",
    ctaHref: "/book?service=cruise",
    fullBleedImage: true,
  },
  {
    testid: "service-module-events",
    eyebrow: "Service · 04",
    imageSrc: "/assets/services/module-d-events.jpg",
    imageSide: "right",
    heading: "VIP Events, Weddings & Proms",
    body: "Phantasm Limousine accommodates a full spectrum of special events: nightlife, proms, weddings, and milestone birthday celebrations. Whether it's a casino tour or a premium night on the town, we transport your party in absolute elegance and style.",
    ctaHref: "/book?service=vip-events",
    secondaryImage: "/assets/services/module-d-events-interior.jpg",
    secondaryCaption: "Inside Every Phantasm Reservation",
    secondaryTestId: "module-d-secondary-thumb",
    fullBleedImage: true,
  },
];

const DISPATCH_ITEMS = [
  "Coordination & Real Time Adjustments",
  "International Events",
  "Professional Presentation & Flight Monitoring",
  "Proven Excellence",
  "Trusted, Reliable & Professional Services",
];

export default function Home() {
  return (
    <Layout>
      {/* HERO */}
      <section
        data-testid="home-hero"
        className="relative min-h-screen w-full flex items-center justify-center overflow-hidden"
      >
        <div
          className="absolute inset-0 z-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/assets/hero.jpg')" }}
          aria-hidden
        />
        <div
          className="absolute inset-0 z-10 bg-gradient-to-b from-[#0B0B0C]/55 via-[#0B0B0C]/65 to-[#0B0B0C]"
          aria-hidden
        />

        <div className="relative z-20 max-w-5xl mx-auto px-6 md:px-12 pt-28 pb-24 text-center">
          <div
            data-testid="hero-eyebrow"
            className="text-[11px] uppercase tracking-[0.4em] text-[#E6E6E6] mb-8 animate-fade-in-up"
          >
            Est. New York · Massachusetts
          </div>
          <h1
            data-testid="hero-headline"
            className="font-serif font-semibold tracking-tighter text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.05] animate-fade-in-up delay-100"
          >
            Phantasm Limousine Service Inc.
          </h1>
          <h2
            data-testid="hero-subheadline"
            className="mt-6 font-serif text-xl md:text-2xl lg:text-3xl text-[#E6E6E6] font-light tracking-tight animate-fade-in-up delay-200"
          >
            Elite Chauffeured Transportation Across New York &amp; Massachusetts.
          </h2>
          <p
            data-testid="hero-subhead-verbatim"
            className="mt-8 max-w-2xl mx-auto text-sm md:text-base text-[#E6E6E6] leading-relaxed font-light animate-fade-in-up delay-300"
          >
            No matter where you want to go, Phantasm Limousine Service's flexibility can
            accommodate all of your needs. Our professional chauffeurs ensure a safe, smooth, and
            distinguished ride across New England.
          </p>

          <div
            data-testid="hero-cta-group"
            className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up delay-400"
          >
            <Link
              to="/book"
              data-testid="cta-reserve-now"
              className="inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-[#E6E6E6] px-9 py-4 transition-all duration-300 font-medium text-xs tracking-[0.25em] uppercase"
            >
              Reserve Now <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
            <Link
              to="/fleet"
              data-testid="cta-view-fleet"
              className="inline-flex items-center justify-center gap-2 bg-transparent border border-white/30 text-white hover:bg-white hover:text-black px-9 py-4 transition-all duration-300 font-medium text-xs tracking-[0.25em] uppercase"
            >
              View Fleet
            </Link>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 text-[10px] uppercase tracking-[0.4em] text-[#8E9AA6] animate-fade-in-up delay-500">
          Scroll
        </div>
      </section>

      {/* Elevate Your Journey banner relocated to bottom of page — directly above Footer. */}

      {/* UPPER GALLERY — dual-image atmospheric showcase */}
      <UpperGallery tiles={GALLERY_TILES} />

      {/* SERVICE MODULES — alternating image cards */}
      <div data-testid="home-service-modules">
        {SERVICE_MODULES.map((m) => (
          <ServiceModule
            key={m.testid}
            {...m}
            secondaryTestId={m.secondaryTestId}
          />
        ))}
      </div>

      {/* REGIONAL DESTINATIONS & VENUES — bridge between modules and dispatch */}
      <section
        data-testid="home-regional-venues"
        className="border-t border-white/10 bg-[#0B0B0C] py-20 md:py-28"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="text-[11px] uppercase tracking-[0.4em] text-[#8E9AA6] mb-6">
            Regional Destinations &amp; Venues
          </div>
          <p
            data-testid="regional-venues-body"
            className="text-base md:text-lg text-[#8E9AA6] font-light leading-relaxed"
          >
            Providing premium airport car service and worldwide connections covering Boston Logan
            (BOS), Manchester Regional Airport (MHT), Bradley International Airport (BDL), Rhode
            Island T.F. Green International Airport (PVD), and John F. Kennedy International
            Airport (JFK), with corporate discounts available for frequent travelers. Enjoy hourly
            luxury transport directly to Gillette Stadium, Fenway Park, TD Garden, MGM Music Hall,
            and regional fine arts tours.
          </p>
          <div className="mt-10 mx-auto w-16 h-px bg-white/20" aria-hidden />
        </div>
      </section>

      {/* 24/7 DISPATCH — checkmark grid */}
      <section
        data-testid="home-dispatch"
        className="border-t border-white/10 bg-[#0B0B0C] py-24 md:py-32"
      >
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16 md:mb-20">
            <div className="text-[11px] uppercase tracking-[0.4em] text-[#8E9AA6] mb-6">
              Operations
            </div>
            <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-white">
              24/7 Dispatch
            </h3>
            <div className="mt-8 mx-auto w-16 h-px bg-white/25" aria-hidden />
          </div>

          <ul
            data-testid="dispatch-grid"
            className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6"
          >
            {DISPATCH_ITEMS.map((item) => (
              <li
                key={item}
                data-testid={`dispatch-item-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                className="flex items-start gap-4 py-2 border-b border-white/[0.06]"
              >
                <Check
                  size={18}
                  strokeWidth={1.75}
                  className="mt-0.5 flex-shrink-0 text-[#E6E6E6]"
                  aria-hidden
                />
                <span className="text-sm md:text-base text-white font-light leading-snug">
                  {item}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* SERVICE REGIONS */}
      <section
        data-testid="home-regions"
        className="border-t border-white/10 bg-[#121214] py-24 md:py-32"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-20">
            <div className="text-[11px] uppercase tracking-[0.4em] text-[#8E9AA6] mb-6">
              Service Regions
            </div>
            <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-white">
              From Manhattan to the Cape.
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20">
            <div data-testid="region-ny" className="border-l border-white/15 pl-8">
              <div className="font-serif text-2xl md:text-3xl text-white mb-6">New York</div>
              <ul className="space-y-3">
                {["New York City", "JFK", "LaGuardia (LGA)", "Newark (EWR)", "The Hamptons"].map(
                  (r) => (
                    <li
                      key={r}
                      className="text-sm md:text-base text-[#E6E6E6] font-light tracking-wide"
                    >
                      {r}
                    </li>
                  )
                )}
              </ul>
            </div>
            <div data-testid="region-ma" className="border-l border-white/15 pl-8">
              <div className="font-serif text-2xl md:text-3xl text-white mb-6">Massachusetts</div>
              <ul className="space-y-3">
                {["Boston", "Logan International", "Chelmsford", "Worcester"].map((r) => (
                  <li
                    key={r}
                    className="text-sm md:text-base text-[#E6E6E6] font-light tracking-wide"
                  >
                    {r}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Elevate Your Journey — full-bleed executive travel / luxury itineraries banner. */}
      <ElevateJourneyBanner />
    </Layout>
  );
}
